// ResolutionsMain.js
// /tickets/resolutions — browse type='resolution' tickets backed by v_resolutions.
//
// Each row shows: resolution title, linked original ticket, summary, resolved_by,
// resolved_at. Expanding the row reveals: root_cause, fix, files_touched,
// prevention, commit_sha. Clicking the original-ticket link navigates to
// /tickets/canvas?ticket=<original_id>. Search/filter: keyword + agent + date.

import { SUPABASE_URL, restHeaders } from '../../../shared/functions/supabaseConfig.js'

// ─── helpers ─────────────────────────────────────────────────────────────────

const _relTime = (isoStr) => {
  if (!isoStr) return ''
  const diffS = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000)
  if (diffS < 60) return `${diffS}s ago`
  if (diffS < 3600) return `${Math.floor(diffS / 60)}m ago`
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`
  return `${Math.floor(diffS / 86400)}d ago`
}

const _agentLabel = (email) => {
  if (!email) return '—'
  // "tickets-agent@symbols.app" → "TICKETS"
  // Also handles bare labels ("DESIGN" → "DESIGN") gracefully.
  if (!email.includes('@')) return email.toUpperCase()
  const local = email.split('@')[0].replace(/-agent$/, '').toUpperCase()
  return local
}

// Normalise a resolvedBy value to an email address.
// Handles legacy rows where agents stored a bare label instead of email
// (e.g. "DESIGN" → "design-agent@symbols.app").
const _normaliseEmail = (val) => {
  if (!val) return null
  if (val.includes('@')) return val
  // bare label — convert to canonical agent email
  return `${val.toLowerCase()}-agent@symbols.app`
}

// Navigate to /tickets/canvas?ticket=<id> using SPA router where available.
const _openOriginal = (root, id) => {
  if (!id) return
  const path = `/tickets/canvas?ticket=${id}`
  try {
    if (root && typeof root.router === 'function') {
      const p = root.router(path, root)
      if (p && typeof p.catch === 'function') p.catch(() => { window.location.href = path })
      return
    }
  } catch (_) {}
  if (typeof window !== 'undefined') window.location.href = path
}

// Fetch a single original ticket for the detail panel when we need full body.
const _fetchOriginal = async (ticketId) => {
  if (!ticketId) return null
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/tickets?id=eq.${ticketId}&select=*&limit=1`,
      { headers: restHeaders() }
    )
    if (!res.ok) return null
    const rows = await res.json()
    return Array.isArray(rows) ? rows[0] || null : null
  } catch (_) { return null }
}

// ─── filter helpers ──────────────────────────────────────────────────────────

// Applies root.resFilterQuery (keyword), root.resFilterAgent, root.resFilterDate
// to s.root.resolutions[]. Returns matching subset.
const _filtered = (root) => {
  const all = root.resolutions || []
  const q = (root.resFilterQuery || '').trim().toLowerCase()
  const agent = root.resFilterAgent || null
  const since = root.resFilterDate ? new Date(root.resFilterDate).getTime() : 0

  return all.filter((r) => {
    // Normalise resolvedBy so legacy label-only rows ("DESIGN") still match
    // the email-keyed filter value ("design-agent@symbols.app").
    if (agent && _normaliseEmail(r.resolvedBy) !== agent) return false
    if (since && r.resolvedAt && new Date(r.resolvedAt).getTime() < since) return false
    if (q) {
      const haystack = [
        r.resolutionTitle,
        r.originalTitle,
        r.summary,
        r.fix,
        r.rootCause,
        r.resolvedBy
      ].filter(Boolean).join(' ').toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })
}

// ─── Expandable resolution row ───────────────────────────────────────────────

export const ResolutionRow = {
  tag: 'div',
  flow: 'y',
  background: 'overlay',
  round: 'A',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  transition: 'border-color 0.15s ease',
  overflow: 'hidden',

  // ── Header ─────────────────────────────────────────────────────────────────
  Head: {
    flow: 'x',
    align: 'center left',
    gap: 'A',
    padding: 'Z A',
    cursor: 'pointer',
    ':hover': { background: 'surface' },

    // Expand indicator
    Chevron: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      color: 'caption',
      transition: 'transform 0.2s ease',
      transform: (el, s) => s.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      text: '›'
    },

    Info: {
      flow: 'y',
      gap: 'V',
      flex: '1',
      overflow: 'hidden',

      TitleLine: {
        flow: 'x',
        align: 'center left',
        gap: 'V',
        flexWrap: 'wrap',

        Title: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          letterSpacing: '-.01em',
          // Strip "Resolution: " prefix for cleaner display
          text: (el, s) => (s.resolutionTitle || '').replace(/^Resolution:\s*/i, '') || '(untitled)'
        },

        OriginalLink: {
          hide: (el, s) => !s.originalTicketId,
          tag: 'span',
          fontSize: 'Y2',
          fontWeight: '600',
          color: 'blue',
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: 'transparent',
          ':hover': { textDecorationColor: 'var(--color-blue)' },
          text: (el, s) => {
            const ref = s.originalExternalId || (s.originalTicketId ? `#${s.originalTicketId}` : '')
            return ref ? `← ${ref}` : ''
          },
          onClick: (e, el, s) => {
            e.stopPropagation()
            _openOriginal(s.root || el.getRoot(), s.originalTicketId)
          }
        }
      },

      Summary: {
        hide: (el, s) => !s.summary,
        tag: 'span',
        fontSize: 'Y1',
        color: 'subtitle',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.summary || ''
      }
    },

    Meta: {
      flow: 'y',
      gap: 'V',
      align: 'center right',
      flexShrink: '0',
      textAlign: 'right',

      Agent: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'cyan',
        letterSpacing: '.04em',
        text: (el, s) => _agentLabel(s.resolvedBy)
      },
      Time: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => _relTime(s.resolvedAt)
      }
    },

    onClick: (e, el, s) => {
      s.update({ expanded: !s.expanded })
    }
  },

  // ── Expanded detail panel ───────────────────────────────────────────────────
  Detail: {
    hide: (el, s) => !s.expanded,
    flow: 'y',
    gap: 'A',
    padding: 'Z A A A',
    borderTopWidth: 'V',
    borderTopStyle: 'solid',
    borderTopColor: 'line',

    // Summary row
    SummaryBlock: {
      hide: (el, s) => !s.summary,
      flow: 'y',
      gap: 'V',
      SummaryLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.08em',
        color: 'caption',
        textTransform: 'uppercase',
        text: 'Summary'
      },
      SummaryText: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y1',
        color: 'subtitle',
        lineHeight: '1.5',
        text: (el, s) => s.summary || ''
      }
    },

    RootCauseBlock: {
      hide: (el, s) => !s.rootCause,
      flow: 'y',
      gap: 'V',
      RcLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.08em',
        color: 'caption',
        textTransform: 'uppercase',
        text: 'Root Cause'
      },
      RcText: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y1',
        color: 'subtitle',
        lineHeight: '1.5',
        text: (el, s) => s.rootCause || ''
      }
    },

    FixBlock: {
      hide: (el, s) => !s.fix,
      flow: 'y',
      gap: 'V',
      FixLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.08em',
        color: 'caption',
        textTransform: 'uppercase',
        text: 'Fix'
      },
      FixText: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y1',
        color: 'green',
        lineHeight: '1.5',
        text: (el, s) => s.fix || ''
      }
    },

    FilesBlock: {
      hide: (el, s) => !(s.filesTouched || []).length,
      flow: 'y',
      gap: 'V',
      FilesLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.08em',
        color: 'caption',
        textTransform: 'uppercase',
        text: 'Files Touched'
      },
      FilesList: {
        flow: 'y',
        gap: 'V',
        childExtends: 'ResolutionFileChip',
        childrenAs: 'state',
        children: (el, s) => (s.filesTouched || []).map((f) => ({ file: f }))
      }
    },

    PreventionBlock: {
      hide: (el, s) => !s.prevention,
      flow: 'y',
      gap: 'V',
      PrevLabel: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.08em',
        color: 'caption',
        textTransform: 'uppercase',
        text: 'Prevention'
      },
      PrevText: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y1',
        color: 'subtitle',
        lineHeight: '1.5',
        text: (el, s) => s.prevention || ''
      }
    },

    // Bottom meta bar: commit sha + link to original
    FooterBar: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',
      marginTop: 'V',
      flexWrap: 'wrap',

      CommitSha: {
        hide: (el, s) => !s.commitSha,
        tag: 'span',
        fontSize: 'Y2',
        fontFamily: 'monospace',
        color: 'caption',
        text: (el, s) => `commit ${(s.commitSha || '').slice(0, 12)}`
      },

      ViewOriginalBtn: {
        hide: (el, s) => !s.originalTicketId,
        tag: 'button',
        type: 'button',
        fontSize: 'Y2',
        fontWeight: '600',
        color: 'blue',
        background: 'transparent',
        borderWidth: '0',
        padding: '0',
        cursor: 'pointer',
        text: (el, s) => {
          const ref = s.originalExternalId || (s.originalTicketId ? `#${s.originalTicketId}` : '')
          const t = s.originalTitle ? `${ref} · ${s.originalTitle}`.slice(0, 60) : ref
          return `View original: ${t}`
        },
        onClick: (e, el, s) => {
          _openOriginal(s.root || el.getRoot(), s.originalTicketId)
        }
      }
    }
  }
}

// Tiny chip for each file path in filesTouched
export const ResolutionFileChip = {
  tag: 'span',
  fontSize: 'Y2',
  fontFamily: 'monospace',
  color: 'caption',
  background: 'surface',
  round: 'V',
  padding: 'V Z',
  display: 'inline-block',
  text: (el, s) => s.file || ''
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

export const ResolutionsFilterBar = {
  flow: 'x',
  align: 'center left',
  gap: 'A',
  padding: 'V 0 A 0',
  flexWrap: 'wrap',

  // Keyword search
  SearchInput: {
    tag: 'input',
    type: 'text',
    placeholder: 'Search resolutions…',
    fontSize: 'Y1',
    padding: 'Z A',
    round: 'V',
    background: 'surface',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    width: '240px',
    ':focus': { borderColor: 'blue', outline: 'none' },
    onInput: (e, el, s) => {
      s.root.update({ resFilterQuery: e.target.value })
    }
  },

  // Agent filter — populated from distinct resolvedBy values.
  // Fix (QA fail #3 / TASK#4488):
  //   A) AllOption lives inside the children factory array — static named
  //      child keys are ignored when childrenAs:'state' + children factory is
  //      active in DOMQL v3.14.
  //   B) Native <option> value attribute uses attr: { value } (not top-level
  //      value:) so that DOMQL's attribute-effect path (element.attr) handles
  //      it.  The value-effect path (definition.value) only fires when value
  //      is directly on the child definition, not inherited via childExtends.
  //      attr: { value } correctly calls node.setAttribute('value', ...) and
  //      handles empty string '' for the AllOption sentinel.
  AgentSelect: {
    tag: 'select',
    fontSize: 'Y1',
    padding: 'Z A',
    round: 'V',
    background: 'surface',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    cursor: 'pointer',
    childExtends: {
      tag: 'option',
      // Bind the native <option> value attribute via attr: { value } so that
      // DOMQL's attribute-effect path (element.attr) handles it rather than the
      // value-effect path (definition.value).  The value-effect only fires when
      // `value` is directly on the child definition, not inherited from extends —
      // which is exactly the childrenAs:'state' + childExtends pattern we use here.
      // attr: { value } calls node.setAttribute('value', ...) reactively, which is
      // the correct mechanism for <option> elements. Empty string '' for the
      // AllOption sentinel; email string for agent items.
      attr: {
        value: (el, s) => (s.agentEmail == null ? '' : s.agentEmail)
      },
      text: (el, s) => (s.agentEmail == null ? 'All agents' : _agentLabel(s.agentEmail))
    },
    childrenAs: 'state',
    // AllOption is the FIRST element in the factory output (sentinel).
    // Agent items follow, keyed by normalised email (labels coerced to email).
    children: (el, s) => {
      const agents = [
        ...new Set(
          (s.root.resolutions || [])
            .map(r => _normaliseEmail(r.resolvedBy))
            .filter(Boolean)
        )
      ]
      return [{ agentEmail: null }, ...agents.map(a => ({ agentEmail: a }))]
    },
    onChange: (e, el, s) => {
      s.root.update({ resFilterAgent: e.target.value || null })
    }
  },

  // Date range — "since"
  DateInput: {
    tag: 'input',
    type: 'date',
    fontSize: 'Y1',
    padding: 'Z A',
    round: 'V',
    background: 'surface',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    cursor: 'pointer',
    title: 'Show resolutions from this date onwards',
    onChange: (e, el, s) => {
      s.root.update({ resFilterDate: e.target.value || null })
    }
  },

  // Clear filters
  ClearBtn: {
    hide: (el, s) => !s.root.resFilterQuery && !s.root.resFilterAgent && !s.root.resFilterDate,
    tag: 'button',
    type: 'button',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'caption',
    background: 'transparent',
    borderWidth: '0',
    padding: 'Z V',
    cursor: 'pointer',
    ':hover': { color: 'title' },
    text: 'Clear',
    onClick: (e, el, s) => {
      s.root.update({ resFilterQuery: null, resFilterAgent: null, resFilterDate: null })
      // Reset DOM inputs
      const bar = el.parent
      if (bar?.SearchInput?.node) bar.SearchInput.node.value = ''
      if (bar?.AgentSelect?.node) bar.AgentSelect.node.value = ''
      if (bar?.DateInput?.node) bar.DateInput.node.value = ''
    }
  }
}

// ─── Main page component ─────────────────────────────────────────────────────

export const TicketsResolutionsMain = {
  extends: 'AppMain',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'AGENT MEMORY · RESOLUTIONS',
      headline: 'Resolutions',
      lede: (el, s) => {
        const all = s.root.resolutions
        if (all == null) return 'Loading resolutions…'
        const n = all.length
        const shown = _filtered(s.root).length
        if (!n) return 'No resolution tickets yet. Agents will populate this as they ship fixes.'
        return shown < n
          ? `${shown} of ${n} resolution${n === 1 ? '' : 's'} · filtered.`
          : `${n} agent resolution${n === 1 ? '' : 's'} · newest first.`
      }
    },

    Container: {
      flow: 'y',
      gap: '0',
      padding: 'Z B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      ResolutionsFilterBar: {},

      // Resolved list
      List: {
        flow: 'y',
        gap: 'V',
        childExtends: 'ResolutionRow',
        childrenAs: 'state',
        children: (el, s) => _filtered(s.root)
      },

      // Empty state
      Empty: {
        hide: (el, s) => {
          const all = s.root.resolutions
          // Hide when not yet loaded (null) or when there are results
          if (all == null) return true
          return _filtered(s.root).length > 0
        },
        flow: 'y',
        align: 'center center',
        gap: 'A',
        padding: 'D',
        textAlign: 'center',

        Icon: {
          extends: 'Icon',
          icon: 'check',
          boxSize: 'D',
          color: 'caption',
          opacity: '.3'
        },
        Title: {
          tag: 'h3',
          margin: '0',
          fontSize: 'B',
          fontWeight: '600',
          color: 'title',
          text: (el, s) => {
            const all = s.root.resolutions || []
            const hasFilter = s.root.resFilterQuery || s.root.resFilterAgent || s.root.resFilterDate
            return all.length && hasFilter ? 'No matches' : 'No resolutions yet'
          }
        },
        Body: {
          tag: 'p',
          margin: '0',
          fontSize: 'A',
          color: 'caption',
          lineHeight: '1.5',
          text: (el, s) => {
            const all = s.root.resolutions || []
            const hasFilter = s.root.resFilterQuery || s.root.resFilterAgent || s.root.resFilterDate
            return all.length && hasFilter
              ? 'Try broadening the search or clearing filters.'
              : 'Agents populate this list when they ship fixes using open_resolution_ticket().'
          }
        }
      }
    }
  }
}

// ─── Suggestions / Improvements / EditRequests — legacy sub-pages ────────────
// These exist as nav targets. They no longer share the ResolutionsMain label
// to avoid collision with the new TicketsResolutionsMain above.

const _cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 'A'
}

export const ResolutionsMain = {
  extends: 'AppMain',
  Header: { extends: 'TicketsHeader' },
  Body: {
    overflow: 'auto',
    Hero: {
      extends: 'PageHero',
      eyebrow: 'RESOLUTIONS',
      headline: 'Suggestions',
      lede: (el, s) => {
        const n = (s.root.suggestions || []).length
        return `${n} AI-generated suggestion${n === 1 ? '' : 's'} from usage analytics.`
      }
    },
    Content: {
      padding: 'B B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',
      ..._cardGrid,
      childExtends: 'SuggestionCard',
      childrenAs: 'state',
      children: (el, s) => s.root.suggestions || []
    }
  }
}

export const ResolutionsImprovementsMain = {
  extends: 'AppMain',
  Header: { extends: 'TicketsHeader' },
  Body: {
    overflow: 'auto',
    Hero: {
      extends: 'PageHero',
      eyebrow: 'RESOLUTIONS · IMPROVEMENTS',
      headline: 'Improvement Requests',
      lede: (el, s) => {
        const n = (s.root.improvementRequests || []).length
        return `${n} AI-generated page${n === 1 ? '' : 's'} from Freestyler awaiting review.`
      }
    },
    Content: {
      padding: 'B B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',
      ..._cardGrid,
      childExtends: 'ImprovementCard',
      childrenAs: 'state',
      children: (el, s) => s.root.improvementRequests || []
    }
  }
}

export const ResolutionsEditRequestsMain = {
  extends: 'AppMain',
  Header: { extends: 'TicketsHeader' },
  Body: {
    overflow: 'auto',
    Hero: {
      extends: 'PageHero',
      eyebrow: 'RESOLUTIONS · EDIT REQUESTS',
      headline: 'Edit Requests',
      lede: (el, s) => {
        const n = (s.root.editRequests || []).length
        const open = (s.root.editRequests || []).filter(r => r.status === 'open').length
        return `${n} pull request${n === 1 ? '' : 's'}${open ? ` · ${open} open` : ''}.`
      }
    },
    Content: {
      padding: 'B B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',
      ..._cardGrid,
      childExtends: 'EditRequestCard',
      childrenAs: 'state',
      children: (el, s) => s.root.editRequests || []
    }
  }
}
